import { useState } from 'react';
import { toast } from 'sonner';
import { auditoriaProveedoresApi } from '../../../lib/api';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import { Check, EyeOff, Pencil } from 'lucide-react';
import { IgnorarDialog } from './IgnorarDialog';

const ORIGEN_LABEL = { alegra_contacts: 'Maestro Alegra', proveedores: 'Maestro ERP', oc: 'OC' };

const FilaNombre = ({ d, onChange }) => {
  const [nombre, setNombre] = useState(d.nombre_propuesto || '');
  const [editando, setEditando] = useState(false);
  const [busy, setBusy] = useState(false);
  const [ignorar, setIgnorar] = useState(false);

  const corregir = async () => {
    setBusy(true);
    try {
      const r = await auditoriaProveedoresApi.corregir(d.id, nombre !== d.nombre_propuesto ? nombre : undefined);
      toast.success(`Corregido: ${r.data.nombre}`, { description: r.data.acciones.join(' · ') });
      onChange();
    } catch (e) {
      toast.error('No se pudo corregir', { description: e.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <tr className="border-b align-top hover:bg-muted/40" data-testid={`fila-nombre-${d.rut_canonico}`}>
      <td className="p-2 font-mono text-xs whitespace-nowrap">{d.rut_canonico}</td>
      <td className="p-2 text-sm">
        <div className="font-medium" data-testid="nombre-erp">{d.nombre_erp}</div>
        <div className="flex flex-wrap gap-1 mt-1">
          {(d.items || []).map((it, i) => (
            <Badge key={i} variant="outline" className="text-[10px] font-normal" title={it.nombre}>
              {ORIGEN_LABEL[it.origen] || it.origen}{it.ocs ? ` ${it.ocs.join(', ')}` : ''}{it.nombre !== d.nombre_erp ? `: ${it.nombre}` : ''}
            </Badge>
          ))}
        </div>
      </td>
      <td className="p-2 text-sm">
        <div className="font-medium" data-testid="nombre-documento">{d.nombre_documento}</div>
        <div className="text-xs text-muted-foreground">{d.documento?.fuente} folio {d.documento?.folio} · {d.documento?.fecha}{d.documento?.fuente === 'RCV' ? ' · sin XML' : ''}</div>
      </td>
      <td className="p-2 text-sm min-w-[260px]">
        {editando ? (
          <Input value={nombre} onChange={(e) => setNombre(e.target.value)} className="h-8 text-sm" data-testid="input-nombre-propuesto" />
        ) : (
          <button type="button" className="text-left hover:underline" onClick={() => setEditando(true)} title="Editar nombre a aplicar" data-testid="nombre-propuesto">
            {nombre} <Pencil className="inline w-3 h-3 text-muted-foreground" />
          </button>
        )}
        {d.contacto_ok && <div className="text-[10px] text-emerald-700 mt-0.5">El maestro Alegra ya está correcto; se alinea el resto a él.</div>}
      </td>
      <td className="p-2 whitespace-nowrap">
        <div className="flex gap-1">
          <Button size="sm" onClick={corregir} disabled={busy || !nombre.trim()} data-testid="btn-corregir">
            <Check className="w-4 h-4 mr-1" /> Corregir
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setIgnorar(true)} disabled={busy} title="Ignorar (caso legítimo)" data-testid="btn-ignorar">
            <EyeOff className="w-4 h-4" />
          </Button>
        </div>
        <IgnorarDialog open={ignorar} onOpenChange={setIgnorar} id={d.id} descripcion={`${d.rut_canonico} · ${d.nombre_erp}`} onDone={onChange} />
      </td>
    </tr>
  );
};

export const TablaNombres = ({ items, onChange, testid }) => {
  if (!items.length) return <p className="text-sm text-muted-foreground py-8 text-center" data-testid={`${testid}-vacio`}>Sin discrepancias pendientes.</p>;
  return (
    <div className="overflow-x-auto rounded-md border">
      <table className="w-full text-sm" data-testid={testid}>
        <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
          <tr>
            <th className="p-2 text-left">RUT</th>
            <th className="p-2 text-left">Nombre en el ERP</th>
            <th className="p-2 text-left">Razón social según documento</th>
            <th className="p-2 text-left">Nombre a aplicar</th>
            <th className="p-2 text-left">Acción</th>
          </tr>
        </thead>
        <tbody>{items.map((d) => <FilaNombre key={d.id} d={d} onChange={onChange} />)}</tbody>
      </table>
    </div>
  );
};
